import { FlatTreeControl } from '@angular/cdk/tree';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MatButton,
  MatButtonModule,
  MatIconButton,
} from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatMenu,
  MatMenuContent,
  MatMenuItem,
  MatMenuTrigger,
} from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  MatTree,
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodePadding,
  MatTreeNodeToggle,
} from '@angular/material/tree';
import {
  BytesConverterPipe,
  FileElement,
  getDocumentIcon,
  getFileTypeAllowed,
  jsonDeepClone,
  SiaArchivePath,
  SiaBaseScriptComponent,
  SiaFileTreeNode,
  SiaInitDirective,
  SiaRouteData,
  SiaRouteMode,
  SiaSearchBoxComponent,
  SiaSizeDetectorService,
  SiaTranslatePipe,
  SiaWindowSize,
} from '@sia/shared';
import { Guid } from 'guid-typescript';
import $ from 'jquery';
import markJs from 'mark.js';
import {
  NgxFileDropComponent,
  NgxFileDropEntry,
  NgxFileDropModule,
} from 'ngx-file-drop';
import { firstValueFrom, forkJoin, Observable, Subscriber } from 'rxjs';
import { SiaRenameDialogComponent } from '../../dialogs/sia-rename-dialog/sia-rename-dialog.component';
import {
  FILE_PREVIEW_CONTEXT,
  FILE_PREVIEW_PROVIDER,
  FilePreviewContext,
} from '../../services/file-preview-provider.interface';
import { FileSystemPreviewProvider } from '../../services/file-system-preview.provider';
import { FilePreviewHandlerComponent } from '../file-preview-handler/file-preview-handler.component';

@Component({
  selector: 'sia-core-file-system',
  templateUrl: './sia-core-file-system.component.html',
  styleUrls: ['./sia-core-file-system.component.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatMenuTrigger,
    MatIconButton,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    MatTreeNodeToggle,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatProgressBar,
    NgxFileDropModule,
    MatButton,
    DatePipe,
    SiaTranslatePipe,
    BytesConverterPipe,
    MatDividerModule,
    MatButtonModule,
    SiaInitDirective,
    MatIconButton,
    SiaSearchBoxComponent,
    NgTemplateOutlet,
    FilePreviewHandlerComponent,
  ],
  providers: [
    { provide: FILE_PREVIEW_PROVIDER, useClass: FileSystemPreviewProvider },
    {
      provide: FILE_PREVIEW_CONTEXT,
      useFactory: (): FilePreviewContext => ({
        archiveId: signal(''),
        isRelativePath: signal(true),
      }),
    },
  ],
})
export class SiaCoreFileSystemComponent extends SiaBaseScriptComponent {
  private readonly siaSizeDetectorService = inject(SiaSizeDetectorService);
  // SiaBaseActionService and SiaSignalrService are injected in the base class (SiaBaseScriptComponent)
  private readonly destroyRef = inject(DestroyRef);
  private readonly previewContext = inject(FILE_PREVIEW_CONTEXT);
  // dialog is injected in the base class (SiaBaseScriptComponent)

  protected readonly getDocumentIcon = getDocumentIcon;

  @ViewChild(NgxFileDropComponent) fileDrop!: NgxFileDropComponent;
  @ViewChild('menuTriggerReference')
  contextMenu!: MatMenuTrigger;

  // Input signals
  readonly showName = input<boolean>(true);
  readonly compactTreeView = input<boolean>(false);
  readonly showFileTree = input<boolean>(false);
  readonly pathDescription = input<string | undefined>(undefined);
  readonly pageDataInput = input<SiaRouteData | undefined>(undefined, {
    alias: 'pageData',
  });
  readonly originalPathInput = input<SiaArchivePath | undefined | null>(
    undefined,
    { alias: 'originalPath' },
  );
  readonly pathInput = input<SiaArchivePath | undefined | null>(undefined, {
    alias: 'path',
  });

  // Internal signals
  private readonly _menuId = signal<string | undefined>(undefined);
  private readonly _pageData = signal<SiaRouteData | undefined>(undefined);
  private readonly _originalPath = signal<SiaArchivePath | undefined | null>(
    undefined,
  );
  readonly #path = signal<SiaArchivePath | undefined | null>(undefined);
  readonly #pageMode = signal<string | undefined>(undefined);
  readonly #filter = signal<string | undefined>(undefined);
  readonly #highlightedFile = signal<string | undefined>(undefined);

  readonly menuX = signal<number>(0);
  readonly menuY = signal<number>(0);
  readonly dropVisible = signal<boolean>(false);
  readonly isLessThan900 = signal<boolean>(false);
  readonly viewMode = signal<'list' | 'folders'>('list');
  readonly fileTreePanelVisible = signal<boolean>(false);
  readonly fileElements = signal<FileElement[]>([]);
  readonly activeUploads = signal<
    { id: string; name: string; progress: number }[]
  >([]);

  // Preview signals
  readonly previewFileId = signal<string | null>(null);
  readonly previewFileType = signal<string>('');
  readonly previewFileName = signal<string>('');
  previewVisible = output<boolean>();

  // Computed signals for public access
  readonly menuId = computed(() => this._menuId());
  readonly pageData = computed(() => this._pageData());
  readonly originalPath = computed(() => this._originalPath());
  readonly path = computed(() => this.#path());
  readonly pageMode = computed(() => this.#pageMode());

  readonly splittedPathParts = computed(() => {
    const path = this.#path()?.path;
    const origPath = this._originalPath()?.path;
    if (!path) return [];
    let pathToSplit = path.replace(origPath ?? '', '');
    pathToSplit = pathToSplit?.startsWith('\\')
      ? pathToSplit.substring(1)
      : pathToSplit;
    return pathToSplit ? (pathToSplit?.split('\\') ?? []) : [];
  });

  treeMarkInstance?: markJs;
  filesMarkInstance?: markJs;

  private _filesTimeout: any = null;
  private _timeouts: any[] = [];

  _transformer = (node: SiaFileTreeNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      path: node.path,
      parent: node.parent,
      selected: node.selected,
      fileExtensions: node.fileExtensions,
      level: level,
      isFolder: node.isFolder,
    };
  };
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );
  treeControl = new FlatTreeControl<any>(
    (node) => node.level,
    (node) => node.expandable,
  );
  fileTree = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  /** Inserted by Angular inject() migration for backwards compatibility */

  constructor() {
    super();
    this.isLessThan900.set(
      (this.siaSizeDetectorService.currentSize?.contentWidth ?? 0) < 900,
    );

    // Effect for pageData input changes
    effect(
      () => {
        const value = this.pageDataInput();
        untracked(() => {
          this._pageData.set(value);
          this._menuId.set(value?.componentId);
          this.#pageMode.set(value?.mode);

          const currentPath = this.#path();
          if (currentPath) {
            this.changePath(currentPath);
          }
        });
      },
      { allowSignalWrites: true },
    );

    // Effect for originalPath input changes
    effect(
      () => {
        const value = this.originalPathInput();
        untracked(() => {
          if (!value || value == null || value === this._originalPath()) {
            return;
          }
          this._originalPath.set(jsonDeepClone(value));
          // Sync preview context with archive data
          this.previewContext.archiveId.set(value.id ?? '');
          this.previewContext.isRelativePath.set(value.isRelativePath ?? true);
        });
      },
      { allowSignalWrites: true },
    );

    // Effect for path input changes
    effect(
      () => {
        const value = this.pathInput();
        untracked(() => this.changePath(value));
      },
      { allowSignalWrites: true },
    );

    // Effect to load files when path changes
    effect(() => {
      const p = this.#path();
      const filter = this.#filter();
      untracked(() => {
        if (p?.path) {
          this.dropVisible.set(false);
          this.fileElements.set([]);
          this.getFiles();
          this.getFileTree();
        }
      });
    });

    this.siaSizeDetectorService.onResize$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((size: SiaWindowSize | null) => {
        this.isLessThan900.set((size?.contentWidth ?? 0) < 900);
      });

    this.siaSignalrService.serviceMessage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((message) => {
        if (message.messageTypeId == 'preview') {
          this.checkForNewPreview(JSON.parse(message.jsonMessage ?? '{}'));
        }
      });

    this.siaRouterService.routeModeChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode: SiaRouteMode) => {
        if (
          mode.guid != this._pageData()?.guid ||
          mode.mode == this.#pageMode()
        ) {
          return;
        }
        this.#pageMode.set(mode.mode);
        const currentPath = this.#path();
        if (currentPath) {
          this.changePath(currentPath);
        }
      });

    // Cleanup timeouts on destroy
    this.destroyRef.onDestroy(() => {
      this._timeouts.forEach((t) => clearTimeout(t));
      this._timeouts = [];
      clearTimeout(this._filesTimeout);
    });
  }

  // Unified method for path changes
  private async changePath(value: SiaArchivePath | undefined | null) {
    if (!value?.id) {
      this._originalPath.set(undefined);
      this.#path.set(undefined);
      return;
    }
    const currentPath = this.#path();
    if (
      currentPath?.isTemporaryPath &&
      !value?.isTemporaryPath &&
      value?.path
    ) {
      await this.moveAll(currentPath, value);
    }

    if (this.#pageMode() === 'create' && !value.path) {
      const newPath = {
        ...value,
        isRelativePath: true,
        path: 'tmp\\' + Guid.create().toString(),
        isTemporaryPath: true,
      };
      this.#path.set(newPath);
      this._originalPath.set(jsonDeepClone(newPath));
    } else {
      this.#path.set(value);
    }
  }

  checkForNewPreview(urlChanged: { oldUrl?: string; newUrl?: string }) {
    if (!urlChanged.newUrl || !urlChanged.oldUrl) {
      return;
    }
    const elements = this.fileElements();
    const fileElement = elements.find(
      (x) =>
        x.previewUrl == urlChanged.oldUrl ||
        (x.previewUrl == urlChanged.newUrl && !x.hasPreview),
    );
    if (fileElement) {
      fileElement.previewUrl = urlChanged.newUrl;
      fileElement.hasPreview = true;
      this.fileElements.set([...elements]);
    }
  }

  private async getFileTree() {
    const origPath = this._originalPath();
    if (!origPath) {
      return;
    }
    origPath.filter = this.#filter();

    const t = setTimeout(async () => {
      const currentOrigPath = this._originalPath();
      if (!currentOrigPath) {
        return;
      }
      try {
        const res: SiaFileTreeNode =
          await this.fileService.getFileTree(currentOrigPath);
        res.selected = true;
        this.fileTree.data = [res];

        this.processTree();
        const treeNodes = $('.mat-tree');
        if (treeNodes && currentOrigPath?.filter) {
          this.treeMarkInstance = new markJs(treeNodes[0]);
          this.treeMarkInstance.mark(currentOrigPath.filter, {
            element: 'mark',
            className: 'highlight',
          });
        } else {
          this.treeMarkInstance?.unmark();
        }
      } catch (error) {
        console.error('Error getting file tree:', error);
      }
    }, 0);
    this._timeouts.push(t);
  }

  navigate(element: FileElement) {
    if (!element.isFolder) {
      return;
    }
    const parent = jsonDeepClone(this.#path());
    const currentPath = this.#path();

    this.changePath({
      ...currentPath,
      path: currentPath?.path + '\\' + this.getElementFullName(element),
      filter: this._originalPath()?.filter,
      parentPath: parent,
    });
  }

  private processTree() {
    if (this.showFileTree()) {
      const currentPath = this.#path();
      let newSelection = this.treeControl.dataNodes.find(
        (x) =>
          x.path?.replaceAll('\\', '') ==
          currentPath?.path?.replaceAll('\\', ''),
      );

      if (newSelection) {
        const current = this.treeControl.dataNodes.find((y) => y.selected);
        this.treeControl.expand(current);
        this.treeControl.expand(newSelection);
        current.selected = false;
        newSelection.selected = true;
        while (newSelection?.parent) {
          newSelection = this.treeControl.dataNodes.find(
            (x) =>
              x.path?.replaceAll('\\', '') ==
              newSelection?.parent?.replaceAll('\\', ''),
          );
          this.treeControl.expand(newSelection);
        }
      }

      if (this._originalPath()?.filter) {
        this.treeControl.dataNodes.forEach((x) => {
          this.treeControl.expand(x);
        });
      }
    }
  }

  enableFileDrop() {
    this.dropVisible.set(true);
    const t = setTimeout(() => {
      this.fileDrop.openFileSelector();
    }, 0);
    this._timeouts.push(t);
  }

  async dropped(files: NgxFileDropEntry[]) {
    files.sort((a, b) => {
      if (
        a.fileEntry.isDirectory === true &&
        b.fileEntry.isDirectory === false
      ) {
        return -1;
      } else if (
        a.fileEntry.isDirectory === false &&
        b.fileEntry.isDirectory === true
      ) {
        return 1;
      } else {
        return 0;
      }
    });

    const currentPath = this.#path();
    const archive: SiaArchivePath = {
      isRelativePath: currentPath?.isRelativePath,
      id: currentPath?.id,
      path: currentPath?.path ?? '',
      isSystemArchive: currentPath?.isSystemArchive,
    };

    const tasks = files.map((droppedFile) => {
      return new Observable<any>((subscriber: Subscriber<any>) => {
        if (droppedFile.fileEntry.isFile) {
          const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
          fileEntry.file((file: File) => {
            this.uploadFile(file, archive, subscriber);
          });
        } else {
          this.fileService
            .createFolder(archive, droppedFile.relativePath)
            .then((res) => {
              subscriber.next(res);
              subscriber.complete();
            });
        }
      });
    });

    await firstValueFrom(forkJoin(tasks));
    await this.getFiles();
    this.getFileTree();
    this.dropVisible.set(false);
  }
  private uploadFile(
    file: File,
    archive: SiaArchivePath,
    subscriber: Subscriber<any>,
  ) {
    const uploadId = Guid.create().toString();
    this.activeUploads.update((u) => [
      ...u,
      { id: uploadId, name: file.name, progress: 0 },
    ]);

    return this.fileService
      .uploadFile(archive, file, false, (progress) => {
        this.activeUploads.update((u) =>
          u.map((up) => (up.id === uploadId ? { ...up, progress } : up)),
        );
      })
      .then((res) => {
        subscriber.next(res);
        subscriber.complete();
        this.activeUploads.update((u) => u.filter((up) => up.id !== uploadId));
        return res;
      })
      .catch((err) => {
        subscriber.error(err);
        this.activeUploads.update((u) => u.filter((up) => up.id !== uploadId));
      });
  }

  async navigateTreeElement(node: any) {
    const originalPath = node.path;
    const currentPath = this.#path();
    if (node.path.replace('\\', '') == currentPath?.path?.replace('\\', '')) {
      return;
    }
    const parent = jsonDeepClone(currentPath);

    this.#highlightedFile.set(originalPath);

    if (node.level == 0) {
      const newPath = jsonDeepClone(this._originalPath()) ?? {};
      if (newPath) {
        newPath.parentPath = parent;
      }
      this.#path.set(newPath);
    } else {
      this.#path.set({
        ...currentPath,
        path: node.isFolder ? node.path : node.parent,
        parentPath: parent,
      });
    }

    this.processTree();
    this.fileTreePanelVisible.set(false);
  }
  modifyFile(element: FileElement) {
    const currentPath = this.#path();
    const file: SiaArchivePath = {
      path: element.id,
      isRelativePath: currentPath?.isRelativePath,
      id: currentPath?.id,
      isSystemArchive: currentPath?.isSystemArchive,
      extension: element.extension,
    };
    this.siaRouterService.navigateMenuWithParams('richedit', [
      { id: 'file', value: file },
    ]);
  }

  async download(element: FileElement) {
    const currentPath = this.#path();
    const originalPath = this._originalPath();
    await this.fileService.downloadFile(
      element.id,
      this.getElementFullName(element),
      originalPath?.isRelativePath ?? true,
      currentPath?.isSystemArchive ?? false,
      originalPath?.id,
    );
  }

  /**
   * Apre la preview fullscreen di un file.
   * Per le cartelle non fa nulla (la navigazione e gestita da click singolo).
   * Per file con tipo non supportato, scarica direttamente.
   */
  openPreview(file: FileElement): void {
    if (file.isFolder) {
      return;
    }
    const fileName = this.getElementFullName(file);
    const fileType = getFileTypeAllowed(file.extension, fileName);

    // Se il tipo non e supportato per la preview, scarica direttamente
    if (!fileType) {
      this.download(file);
      return;
    }

    this.previewFileId.set(file.id);
    this.previewVisible.emit(true);
    this.previewFileType.set(fileType);
    this.previewFileName.set(fileName);
  }

  /**
   * Chiude la preview fullscreen resettando i signal.
   */
  closePreview(): void {
    this.previewFileId.set(null);
    this.previewVisible.emit(false);
    this.previewFileType.set('');
    this.previewFileName.set('');
  }

  async deleteElement(element: FileElement) {
    const currentPath = this.#path();
    const originalPath = this._originalPath();

    const res = await this.fileService.deleteFile(
      element.id,
      originalPath?.isRelativePath ?? true,
      currentPath?.isSystemArchive ?? false,
      originalPath?.id,
    );
    if (!res) {
      return;
    }
    await this.getFiles();
    this.getFileTree();
  }

  async createFolder() {
    const dialogRef = this.dialog.open(SiaRenameDialogComponent, {
      data: { fileName: '', title: 'Nuova cartella' },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    const currentPath = this.#path();
    if (res && currentPath) {
      await this.fileService.createFolder(currentPath, res);
      await this.getFiles();
      this.getFileTree();
    }
  }

  async navigateUp() {
    const currentPath = this.#path();
    if (!currentPath?.parentPath) {
      return;
    }
    this.#path.set(currentPath.parentPath);
    await this.getFiles();
    this.processTree();
  }

  showFileTreePanel() {
    this.fileTreePanelVisible.update((v) => !v);
  }

  async moveElement(element: FileElement, moveTo: FileElement) {
    const currentPath = this.#path();
    if (!element.name || !moveTo.name || !moveTo.parent || !currentPath) {
      return;
    }
    const res = await this.fileService.move(
      this.getElementFullName(element),
      moveTo.name,
      moveTo.parent,
      currentPath,
    );
    if (!res) {
      return;
    }
    await this.getFiles();
  }

  async moveElementToParent(element: FileElement) {
    const currentPath = this.#path();
    if (!element.name || !currentPath?.parentPath || !currentPath) {
      return;
    }
    const res = await this.fileService.moveUp(
      this.getElementFullName(element),
      element.parent ?? '',
      currentPath,
    );
    if (!res) {
      return;
    }
    await this.getFiles();
  }

  async openRenameDialog(element: FileElement) {
    const dialogRef = this.dialog.open(SiaRenameDialogComponent, {
      data: { fileName: element.name },
    });
    const res = await firstValueFrom(dialogRef.afterClosed());
    if (res) {
      const currentPath = this.#path();
      const file: SiaArchivePath = {
        path: element.id,
        isRelativePath: currentPath?.isRelativePath,
        id: currentPath?.id,
        isSystemArchive: currentPath?.isSystemArchive,
        newName: res + (element.extension ?? ''),
      };
      const renamed = await this.fileService.renameFile(file);
      if (!renamed) {
        return;
      }
      await this.getFiles();
      this.getFileTree();
    }
  }

  preventDefault(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  getElementFullName(element: FileElement) {
    return (element.name ?? '') + (element.extension ?? '');
  }

  async getFiles(selectedPath: string | null = null): Promise<void> {
    this.fileElements.set([]);
    clearTimeout(this._filesTimeout);

    // Default badge to hidden
    this.siaBaseActionService.showActionBadge(
      this._menuId() ?? '',
      this.siaRouterService.getLatestRoutingData()?.guid ?? '',
      'file-system-panel',
      false,
    );

    return new Promise<void>((resolve) => {
      this._filesTimeout = setTimeout(async () => {
        const currentPath = this.#path();
        if (!currentPath || !currentPath.path) {
          resolve();
          return;
        }

        const pathWithFilter = { ...currentPath };
        pathWithFilter.filter = this.#filter();
        const requestPath = { ...pathWithFilter, parentPath: {} };

        try {
          let files: FileElement[];
          if (!currentPath.isSystemArchive) {
            files = await this.fileService.getFiles(requestPath);
            this.fileElements.set(files);
          } else {
            files = await this.fileService.getSystemFiles(requestPath);
            files.forEach((x) => {
              x.enableEdit = '.docx|.txt|.doc|.odt'.includes(x.extension ?? '');
            });
            this.fileElements.set(files);
          }

          const t = setTimeout(async () => {
            const origPath = this._originalPath();
            const hasContent = await this.checkHasActualContent();

            this.siaBaseActionService.showActionBadge(
              this._menuId() ?? '',
              this.siaRouterService.getLatestRoutingData()?.guid ?? '',
              'file-system-panel',
              hasContent,
            );

            const treeNodes = $('.content');
            if (treeNodes && origPath?.filter) {
              this.filesMarkInstance = new markJs(treeNodes[0]);
              this.filesMarkInstance.mark(origPath.filter, {
                element: 'mark',
                className: 'highlight',
              });
            } else {
              this.filesMarkInstance?.unmark();
            }

            const highlightPath = this.#highlightedFile() || selectedPath;
            if (highlightPath) {
              const $element = $('.sia-file-list-item').filter(function () {
                return $(this).attr('pathname') === highlightPath;
              });
              if ($element && $element.length > 0) {
                $element[0].classList.add('highlight');
                const highlightTimeout = setTimeout(() => {
                  $element[0].classList.remove('highlight');
                }, 3000);
                this._timeouts.push(highlightTimeout);
                $element[0].scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
              this.#highlightedFile.set(undefined);
            }
          }, 0);
          this._timeouts.push(t);
        } catch (error) {
          console.error('Error getting files:', error);
        }

        resolve();
      }, 0);
    });
  }

  private async checkHasActualContent(): Promise<boolean> {
    const elements = this.fileElements();
    if (!elements || elements.length === 0) {
      return false;
    }

    const currentPath = this.#path();
    if (!currentPath?.id) return false;

    // if there's at least one file we already have content, no need to ask the server
    if (elements.some((e) => !e.isFolder)) {
      return true;
    }

    // only folders remain; find the first one that could contain children and query once
    const firstFolder = elements.find(
      (e) => e.isFolder && e.childCount && e.childCount > 0,
    );
    if (firstFolder) {
      // single API call – if it has content return that result
      return this.pathHasContent(currentPath.id, firstFolder.id);
    }

    return false;
  }

  foldersExist(el: any) {
    return (
      this.fileElements().filter((x) => x.isFolder && x.id != el.id).length > 0
    );
  }

  hasChild = (_: number, node: any) => node.expandable;

  onContextMenu(event: MouseEvent, file: FileElement) {
    event.preventDefault();
    this.menuX.set(event.clientX);
    this.menuY.set(event.clientY);
    this.contextMenu.menuData = { element: file };
    const t = setTimeout(() => {
      this.contextMenu.openMenu();
    });
    this._timeouts.push(t);
  }

  async manageResearch(filter?: string) {
    this.#filter.set(filter);
    this.getFileTree();
    await this.getFiles();
  }

  async moveAll(tmpPath: SiaArchivePath, finalPath: SiaArchivePath) {
    await this.fileService.moveAll(tmpPath, finalPath);
    while (tmpPath?.parentPath?.isTemporaryPath) {
      tmpPath = tmpPath.parentPath;
    }
    await this.getFiles();
    this.getFileTree();
  }

  async onPasteClick() {
    const clipboardItems = await navigator.clipboard.read();
    for (const clipboardItem of clipboardItems) {
      const currentPath = this.#path();
      if (!currentPath) {
        return;
      }
      const blob = await clipboardItem.getType('image/png');
      const extension = '.png';
      const mimeType = 'image/png';
      const dialogRef = this.dialog.open(SiaRenameDialogComponent, {
        data: { title: 'Nome immagine' },
      });
      const res = await firstValueFrom(dialogRef.afterClosed());
      if (!res) {
        return;
      }
      const file = new File([blob], res + extension, {
        type: mimeType,
      });
      await this.uploadFile(file, currentPath, new Subscriber());
      await this.getFiles();
      this.getFileTree();
    }
  }

  async navigateToPathPart(partIndex: number) {
    if (partIndex == -1) {
      await this.changePath(jsonDeepClone(this._originalPath()));
      return;
    }
    const parts = this.splittedPathParts();
    let newPath = parts.slice(0, partIndex + 1).join('\\');
    newPath = this._originalPath()?.path + '\\' + newPath;
    await this.changePath({
      ...this.#path(),
      path: newPath,
    });
  }
}
